/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import * as stagesDivorce from "../../../Stages/StagesDivorce";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { formValue } from "../../../formServiceFunctions";
import {
  beaSafeIdFieldDefault,
  instituteReferenceDefault,
  dateFieldDefault,
  emailDefault,
  faxNumberDefault,
  intFieldDefault,
  postalCodeDefault,
  textFieldDefault,
} from "../../../validationRules";
import InputAdornment from "@mui/material/InputAdornment";
import React from "react";
import _ from "lodash";
import nationalities from "../../../nationalities";
import { allDataPageCourtFields } from "../allDataPageCourtFields";
import { hasActiveCourtAppointment } from "../../ProductService/productService";
import personFields from "../modules/personFields";
import addressFields from "../modules/addressFields";
import basicCaseInsuranceFields from "../modules/basicCaseInsuranceFields";
import { UseForm } from "../../../../types/UseForm";
import { ProductUnion } from "../../../../types/ProductUnion";
import { Divorce } from "../../../../types/Products/FamilyLaw/Divorce";
import { getCourtAppointmentFields } from "../modules/courtAppointmentFields";

const childrenFields = () => {
  let fields = [];

  for (let i = 0; i < 6; i++) {
    fields.push(
      {
        type: "ValidatorTextField",
        path: "divorce.children[" + i + "].name",
        validators: textFieldDefault,
        isHidden: ({ product }: { product: ProductUnion }) => product.numberChildren < i + 1,
        isDisabled: ({ product }: { product: ProductUnion }) => product.stage > stagesDivorce.STAGE_READY,
      },
      {
        type: "ValidatorSelect",
        path: "divorce.children[" + i + "].livesWith",
        options: () => [{ value: "petitioner" }, { value: "partner" }],
        isHidden: ({ product }: { product: ProductUnion }) => product.numberChildren < i + 1,
        isDisabled: ({ product }: { product: ProductUnion }) => product.stage > stagesDivorce.STAGE_READY,
      },
    );
  }
  return fields;
};

const partner = {
  label: "Gegenseite - Ehepartner",
  elements: [
    {
      type: "ValidatorSelect",
      path: "divorce.partner.gender",
      isDisabled: ({ product }: { product: ProductUnion }) => product.stage > stagesDivorce.STAGE_READY,
      options: () => [{ value: "male" }, { value: "female" }],
    },
    {
      type: "ValidatorTextField",
      path: "divorce.partner.givenName",
      isDisabled: ({ product }: { product: ProductUnion }) => product.stage > stagesDivorce.STAGE_READY,
      validators: [...textFieldDefault],
    },
    {
      type: "ValidatorTextField",
      path: "divorce.partner.familyName",
      isDisabled: ({ product }: { product: ProductUnion }) => product.stage > stagesDivorce.STAGE_READY,
      validators: [...textFieldDefault],
    },
    {
      type: "ValidatorSelect",
      path: "divorce.partner.nationality",
      isDisabled: ({ product }: { product: ProductUnion }) => product.stage > stagesDivorce.STAGE_READY,
      options: () =>
        _.map(nationalities, (nationality) => {
          return {
            value: nationality.label,
            label: nationality.label,
          };
        }),
    },
    {
      type: "ValidatorTextField",
      path: "divorce.partner.residenceAddress.streetAddress",
      validators: [...textFieldDefault],
    },
    {
      type: "ValidatorTextField",
      path: "divorce.partner.residenceAddress.postalCode",
      validators: [...textFieldDefault, postalCodeDefault],
    },
    {
      type: "ValidatorTextField",
      path: "divorce.partner.residenceAddress.addressLocality",
      validators: [...textFieldDefault],
    },
  ],
};

const opponentLawyer = {
  label: "Anwalt Gegenseite",
  elements: [
    {
      type: "BeaLawyerImportButton",
      path: "pathPropNotUsedButRequired",
      additionalProps: {
        fullWidth: true,
      },
    },
    ...personFields({ path: `divorce.opponentLawyer` }),
    {
      type: "ValidatorTextField",
      path: `divorce.opponentLawFirm.organization.name`,
    },
    {
      type: "ValidatorTextField",
      path: `divorce.opponentLawFirm.organization.mainEmail.email`,
      validators: emailDefault,
    },
    {
      type: "ValidatorTextField",
      path: `divorce.opponentLawFirm.organization.telephone`,
    },
    {
      type: "ValidatorTextField",
      path: `divorce.opponentLawFirm.organization.fax`,
      validators: faxNumberDefault,
      additionalProps: {
        InputProps: {
          startAdornment: <InputAdornment position="start">+49</InputAdornment>,
        },
      },
    },
    {
      type: "ValidatorTextField",
      path: `divorce.opponentLawFirm.organization.beaSafeId`,
      validators: beaSafeIdFieldDefault,
    },
    ...addressFields({ path: `divorce.opponentLawFirm.organization.address` }),
    {
      type: "ValidatorTextField",
      path: `divorce.referenceOpponentLawyer`,
    },
  ],
};

export const fullFormDefinition = (product: Divorce) => ({
  product: {
    sections: [
      {
        label: "Angaben Antragsteller",
        elements: [
          {
            type: "ValidatorSelect",
            path: "divorce.petitioner.gender",
            options: () => [{ value: "male" }, { value: "female" }, { value: "diverse" }],
          },
          {
            type: "ValidatorTextField",
            path: "divorce.petitioner.givenName",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorTextField",
            path: "divorce.petitioner.familyName",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorSelect",
            path: "divorce.petitioner.nationality",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            options: () =>
              _.map(nationalities, (nationality) => {
                return {
                  value: nationality.label,
                  label: nationality.label,
                };
              }),
          },
          {
            type: "ValidatorTextField",
            path: "divorce.petitioner.residenceAddress.streetAddress",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorTextField",
            path: "divorce.petitioner.residenceAddress.postalCode",
            validators: [...textFieldDefault, postalCodeDefault],
          },
          {
            type: "ValidatorTextField",
            path: "divorce.petitioner.residenceAddress.addressLocality",
            validators: [...textFieldDefault],
          },
        ],
      },
      {
        label: "Letzte gemeinsame Adresse",
        elements: [
          {
            type: "ValidatorTextField",
            path: "divorce.commonAddress.streetAddress",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorTextField",
            path: "divorce.commonAddress.postalCode",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            validators: [...textFieldDefault, postalCodeDefault],
          },
          {
            type: "ValidatorTextField",
            path: "divorce.commonAddress.addressLocality",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            validators: [...textFieldDefault],
          },
        ],
      },
      {
        label: "Ehe und Scheidung",
        elements: [
          {
            type: "ValidatorDateField",
            path: "divorce.dateMarriage",
            validators: [...dateFieldDefault],
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_SUBMITTED,
          },
          {
            type: "ValidatorTextField",
            path: "divorce.cityMarriage",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_SUBMITTED,
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorSelect",
            path: "divorce.numberChildren",
            options: () => [
              { value: 0 },
              { value: 1 },
              { value: 2 },
              { value: 3 },
              { value: 4 },
              { value: 5 },
              { value: 6 },
            ],
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_INCOMPLETE,
          },
          {
            type: "ValidatorSelect",
            path: "divorce.statusDivorce",
            options: () => [
              { value: "__null__" },
              { value: "mutuallyAgreed" },
              { value: "unsure" },
              { value: "debatable" },
            ],
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_SUBMITTED,
          },
          {
            type: "ValidatorDateField",
            path: "divorce.dateSeparation",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_SUBMITTED,
            validators: [...dateFieldDefault],
          },
          {
            type: "ValidatorSelect",
            path: "divorce.movedOut",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_SUBMITTED,
            options: () => [{ value: "petitioner" }, { value: "partner" }, { value: "nobody" }, { value: "both" }],
          },
          {
            type: "ValidatorDateField",
            path: "divorce.dateMovedOut",
            validators: dateFieldDefault,
            isDisabled: ({ values, product }: { values: UseForm["values"]; product: Divorce }) =>
              formValue(values, "divorce.movedOut") === "nobody" || product.stage > stagesDivorce.STAGE_SUBMITTED,
          },
        ],
      },
      {
        label: "Finanzen",
        elements: [
          {
            type: "ValidatorNumberField",
            path: "divorce.netIncomePetitioner",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            fieldType: "money",
            numberType: "int",
          },
          {
            type: "ValidatorNumberField",
            path: "divorce.netIncomePartner",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            fieldType: "money",
            numberType: "int",
          },
          {
            type: "ValidatorSelect",
            path: "divorce.pensionsQuestions",
            options: () => trueFalseDefaultOptions("divorce.pensionsQuestions"),
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
          },
          {
            type: "ValidatorSelect",
            path: "divorce.pensionsExcluded",
            options: () => trueFalseDefaultOptions("divorce.pensionsExcluded"),
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_SUBMITTED,
          },
          {
            type: "ValidatorSelect",
            path: "divorce.shortMarriage",
            isDisabled: () => true,
            options: () => trueFalseDefaultOptions("divorce.shortMarriage"),
          },
          {
            type: "ValidatorTextField",
            path: "divorce.numberPensions",
            validators: [...intFieldDefault],
            isDisabled: ({ values, product }: { values: UseForm["values"]; product: Divorce }) =>
              formValue(values, "divorce.pensionsExcluded") === true || product.stage > stagesDivorce.STAGE_READY,
          },
          {
            type: "ValidatorTextField",
            path: "divorce.numberPensionsPartner",
            validators: [...intFieldDefault],
            isDisabled: ({ values, product }: { values: UseForm["values"]; product: Divorce }) =>
              formValue(values, "divorce.pensionsExcluded") === true || product.stage > stagesDivorce.STAGE_READY,
          },
          {
            type: "ValidatorSelect",
            path: "divorce.hasProcessCostSupport",
            options: () => trueFalseDefaultOptions("divorce.hasProcessCostSupport"),
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            isHidden: ({ product }: { product: Divorce }) => product.paymentType === "insurance",
          },
          {
            type: "ValidatorSelect",
            path: "divorce.paymentType",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            options: () => [
              { value: "__null__" },
              { value: "insurance" },
              { value: "processCostSupport" },
              { value: "installment" },
              { value: "private" },
            ],
          },
        ],
      },
      {
        label: "",
        elements: [
          {
            type: "ValidatorTextField",
            path: "divorce.personalTextMessage",
            isDisabled: () => true,
            additionalProps: {
              multiline: true,
              rows: 12,
              fullWidth: true,
            },
          },
        ],
      },
      {
        label: "Gemeinsame Kinder",
        isHidden: ({ values }: { values: UseForm["values"] }) => formValue(values, "divorce.numberChildren") === "0",
        elements: childrenFields(),
      },
    ],
  },
  additional: {
    sections: [
      {
        label: "",
        elements: [
          {
            type: "ValidatorTextField",
            path: "divorce.reference",
            isDisabled: () => true,
          },
          {
            type: "ValidatorTextField",
            path: "divorce.powerOfAttorney",
            isDisabled: () => true,
          },
          {
            type: "ValidatorDateField",
            path: "divorce.applicationSubmittedDate",
            isDisabled: () => true,
          },
          {
            type: "ValidatorTextField",
            path: "divorce.courtReference",
            validators: instituteReferenceDefault,
          },
          {
            type: "ValidatorTextField",
            path: "divorce.marriageRegistrationNumber",
            validators: textFieldDefault,
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
          },
          {
            type: "ValidatorDateField",
            path: "divorce.processCostSupportVerified",
            validators: dateFieldDefault,
            isHidden: ({ values, product }: { values: UseForm["values"]; product: Divorce }) =>
              formValue(values, "divorce.hasProcessCostSupport") === false || product.paymentType === "insurance",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
          },
        ],
      },
      {
        label: "Ratenzahlung",
        isHidden: ({ product }: { product: Divorce }) =>
          product.hasProcessCostSupport || product.paymentType === "insurance",
        elements: [
          {
            type: "ValidatorSelect",
            path: "divorce.installment",
            isDisabled: ({ product }: { product: Divorce }) => product.stage > stagesDivorce.STAGE_READY,
            options: () => trueFalseDefaultOptions("divorce.hasProcessCostSupport"),
          },
          {
            type: "ValidatorSelect",
            path: "divorce.installmentRateOverdue",
            options: () => trueFalseDefaultOptions("divorce.installmentRateOverdue"),
            isHidden: ({ values }: { values: UseForm["values"] }) => formValue(values, "divorce.installment") !== true,
          },
          {
            type: "ValidatorDateField",
            path: "divorce.installmentRateOverdueSince",
            validators: dateFieldDefault,
            isHidden: ({ values }: { values: UseForm["values"] }) => formValue(values, "divorce.installment") !== true,
          },
        ],
      },
      {
        label: "Gerichtsgebühr",
        elements: [
          {
            type: "ValidatorDateField",
            path: "divorce.courtFeeInvoiceReceivedDate",
            validators: dateFieldDefault,
            isDisabled: ({ product }: { product: Divorce }) => product.stage !== stagesDivorce.STAGE_SUBMITTED,
          },
          {
            type: "ValidatorDateField",
            path: "divorce.courtFeePaidDate",
            validators: dateFieldDefault,
            isDisabled: ({ product }: { product: Divorce }) => product.stage !== stagesDivorce.STAGE_SUBMITTED,
          },
        ],
      },
      {
        label: "Zum Scheidungstermin",
        elements: [
          {
            type: "ValidatorNumberField",
            path: "divorce.valueInDisputeReal",
            fieldType: "money",
            numberType: "float",
            isDisabled: ({ product }: { product: Divorce }) =>
              ![
                stagesDivorce.STAGE_HEARING_PAID,
                stagesDivorce.STAGE_DIVORCE_DONE,
                stagesDivorce.STAGE_DATE_SET,
              ].includes(product.stage),
          },
        ],
      },
      {
        label: product.courtAppointments.length === 1 ? "Gerichtstermin" : "Gerichtstermine",
        isHidden: ({ product }: { product: Divorce }) => product.courtAppointments.length === 0,
        elements: getCourtAppointmentFields(product),
      },
    ],
  },
  processParticipants: {
    asAccordion: true,
    sections: [
      partner,
      {
        label: "Versicherung",
        isHidden: ({ product }: { product: Divorce }) => !product.acquisitionPartner,
        elements: [
          ...basicCaseInsuranceFields({ path: "divorce.caseInsurance", product: product }),
          {
            type: "ValidatorDateField",
            path: "divorce.insuranceCoverageDate",
            validators: dateFieldDefault,
            isDisabled: ({ product }: { product: Divorce }) => product.stage === stagesDivorce.STAGE_COMPLETED,
          },
        ],
      },
      opponentLawyer,
    ],
  },
  additionalProcessParticipants: {
    sections: [
      {
        label: "Neu hinzufügen",
        isHidden: () => false,
        elements: [
          {
            type: "AddProcessParticipantForm",
            path: "addProcessParticipant", // just here because its also used as key for react
            additionalProps: {
              fullWidth: true,
            },
          },
        ],
      },
      {
        label: "Personen",
        isHidden: ({ product }: { product: Divorce }) => product.processParticipantsPeople.length < 1,
        elements: [
          {
            type: "ProcessParticipantsPeopleForm",
            path: `${product.productClassName}.relatedPeople`,
            additionalProps: {
              fullWidth: true,
            },
          },
        ],
      },
      {
        label: "Unternehmen",
        isHidden: ({ product }: { product: Divorce }) => product.processParticipantsOrganizations.length < 1,
        elements: [
          {
            type: "ProcessParticipantsOrganizationsForm",
            path: `${product.productClassName}.relatedOrganizations`,
            additionalProps: {
              fullWidth: true,
            },
          },
        ],
      },
    ],
    withoutSubmit: true,
  },
  court: {
    sections: [
      {
        elements: [
          {
            type: "CourtPicker",
            path: "divorce.responsibleCourt",
            isDisabled: ({ product }: { product: Divorce }) =>
              product.stage < stagesDivorce.STAGE_CHECKED || hasActiveCourtAppointment(product.courtAppointments),
            additionalProps: {
              fullWidth: true,
              courtTypes: ["Familiengericht", "Mahngericht"],
            },
          },
          ...allDataPageCourtFields("divorce", "responsibleCourt"),
        ],
      },
    ],
  },
});
